import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeftLong } from "react-icons/fa6";
import useCreateBlog from "../hooks/useCreateBlog";
import useUploadFiles from "../../../hooks/useUploadFiles";
import RichTextEditor from "../../../component/element/forms/RichTextEditor";
import TextInput from "../../../component/element/forms/TextInput";
import SelectOption from "../../../component/element/forms/SelectOption";
import Upload from "../../../component/modals/upload";
import Button from "../../../component/element/forms/Button";
import { Switch } from "../../../component/ui/Switch";
import { create_blog } from "../../../services/apis/blog";
import { get_categories } from "../../../services/apis/category";
import { useAppDispatch, useAppSelector } from "../../../store";

const CreateBlog: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const categories = useAppSelector(({ category }) => category.categories);
  const loading = useAppSelector(({ blog }) => blog.loading);
  const [imageUploadPath, setImageUploadPath] = useState("");
  const { loader, uploadFile, deleteFiles } = useUploadFiles();
  const { data, error, setData, setError, onValidate } = useCreateBlog();

  const handleSubmit = async () => {
    if (!onValidate()) return;
    if (await create_blog(data, dispatch)) navigate("/blog");
  };

  React.useEffect(() => {
    get_categories(dispatch);
  }, [dispatch]);

  return (
    <div className="pb-12">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-bold text-xl mb-0">Create Blog</h2>
        <Button
          title="Back"
          onClick={() => navigate(-1)}
          children={<FaArrowLeftLong className="mr-2" />}
          className="px-4"
        />
      </div>

      <div className="bg-white p-6 rounded-xl space-y-6">
        <Upload
          title="Upload Feature Image"
          label="JPG & PNG"
          name="feature_image"
          loading={loader}
          status={error.feature_image}
          completed={Boolean(data.feature_image)}
          imgPreview={data.feature_image}
          onChange={async (file) => {
            const uploaded = await uploadFile(file, "blog", "image");
            if (uploaded.length > 0) {
              setData((previous) => ({
                ...previous,
                feature_image: uploaded[0].file_url,
              }));
              setError((previous) => ({ ...previous, feature_image: "" }));
              setImageUploadPath(uploaded[0].path);
            }
          }}
          onDelete={async () => {
            if (!imageUploadPath) return;
            if (await deleteFiles(imageUploadPath)) {
              setData((previous) => ({ ...previous, feature_image: "" }));
              setImageUploadPath("");
            }
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TextInput
            label="Title"
            name="title"
            placeholder="Enter blog title"
            value={data.title}
            type="text"
            status={error.title}
            onChange={(event) => {
              setData((previous) => ({
                ...previous,
                title: event.target.value,
              }));
              setError((previous) => ({ ...previous, title: "" }));
            }}
          />
          <TextInput
            label="Slug"
            name="slug"
            placeholder="blog-url-slug"
            value={data.slug}
            type="text"
            status={error.slug}
            onChange={(event) => {
              setData((previous) => ({
                ...previous,
                slug: event.target.value,
              }));
              setError((previous) => ({ ...previous, slug: "" }));
            }}
          />
          <TextInput
            label="Excerpt"
            name="excerpt"
            placeholder="Short blog summary"
            value={data.excerpt}
            type="text"
            status={error.excerpt}
            onChange={(event) => {
              setData((previous) => ({
                ...previous,
                excerpt: event.target.value,
              }));
              setError((previous) => ({ ...previous, excerpt: "" }));
            }}
          />
          <div>
            <SelectOption
              items={categories}
              label="Category"
              placeholder="Select Category"
              value={data.category_id ? String(data.category_id) : ""}
              onChange={(value) => {
                setData((previous) => ({
                  ...previous,
                  category_id: Number(value),
                }));
                setError((previous) => ({ ...previous, category_id: "" }));
              }}
            />
            {error.category_id && (
              <p className="mt-1 text-xs text-red-500">
                A category is required
              </p>
            )}
          </div>
        </div>

        <RichTextEditor
          label="Blog Content"
          value={data.body}
          error={error.body ? "Blog content is required" : undefined}
          onChange={(body) => {
            setData((previous) => ({ ...previous, body }));
            setError((previous) => ({ ...previous, body: "" }));
          }}
        />

        <div className="flex items-center gap-3">
          <Switch
            checked={data.is_published}
            onCheckedChange={(is_published) =>
              setData((previous) => ({ ...previous, is_published }))
            }
            aria-label="Publish blog"
          />
          <span className="text-sm font-medium">Publish blog</span>
        </div>

        <div className="flex justify-end">
          <Button
            title="Create Blog"
            type="button"
            loading={loading}
            onClick={handleSubmit}
            className="w-36"
          />
        </div>
      </div>
    </div>
  );
};

export default CreateBlog;
